import React, { useCallback, useEffect, useRef, useState } from 'react';
import { CYBERSECURITY_STATS } from '../../../data/statistics';

export default function AnimatedStatistics() {
  const [animatedValues, setAnimatedValues] = useState({
    attacks: 0,
    cost: 0,
    jobs: 0,
    humanError: 0,
    detectionTime: 0,
    breachCost: 0
  });

  const observerRef = useRef(null);
  const timeoutsRef = useRef([]);
  const intervalsRef = useRef([]);

  const animateValue = useCallback((key, target, stepDuration, steps) => {
    let current = 0;
    const increment = target / steps;

    const timer = setInterval(() => {
      current += increment;
      if (current >= target) {
        current = target;
        clearInterval(timer);
        // Retirer le timer de la liste quand il se termine
        intervalsRef.current = intervalsRef.current.filter(t => t !== timer);
      }
      setAnimatedValues(prev => ({
        ...prev,
        [key]: current
      }));
    }, stepDuration);

    // Ajouter le timer à la liste pour pouvoir l'annuler
    intervalsRef.current.push(timer);
  }, []);

  const startAnimations = useCallback(() => {
    // Annuler toutes les animations en cours
    timeoutsRef.current.forEach(timeout => clearTimeout(timeout));
    intervalsRef.current.forEach(interval => clearInterval(interval));
    timeoutsRef.current = [];
    intervalsRef.current = [];

    const targets = {
      attacks: 600, // 600M
      cost: 10.5, // $10.5T
      jobs: 3.5, // 3.5M
      humanError: 95, // 95%
      detectionTime: 287, // 287 jours
      breachCost: 4.88 // $4.88M
    };

    const duration = 1500; // 1.5 secondes
    const steps = 60;
    const stepDuration = duration / steps;

    Object.keys(targets).forEach((key, index) => {
      const timeout = setTimeout(() => {
        animateValue(key, targets[key], stepDuration, steps);
      }, index * 100); // Délai échelonné entre chaque animation

      timeoutsRef.current.push(timeout);
    });
  }, [animateValue]);

  useEffect(() => {
    // Fonction pour réinitialiser et relancer l'animation
    const resetAndAnimate = () => {
      // Annuler toutes les animations en cours
      timeoutsRef.current.forEach(timeout => clearTimeout(timeout));
      intervalsRef.current.forEach(interval => clearInterval(interval));
      timeoutsRef.current = [];
      intervalsRef.current = [];

      setAnimatedValues({
        attacks: 0,
        cost: 0,
        jobs: 0,
        humanError: 0,
        detectionTime: 0,
        breachCost: 0
      });

      setTimeout(() => {
        startAnimations();
      }, 300);
    };

    // Fonction pour vérifier si notre slide est active
    const checkSlideState = () => {
      if (observerRef.current) {
        const slide = observerRef.current;
        const isPresent = slide.classList.contains('present');
        const isNotHidden = !slide.hasAttribute('hidden');

        if (isPresent && isNotHidden) {
          resetAndAnimate();
        } else {
          // Arrêter les animations si on n'est pas sur la slide
          timeoutsRef.current.forEach(timeout => clearTimeout(timeout));
          intervalsRef.current.forEach(interval => clearInterval(interval));
          timeoutsRef.current = [];
          intervalsRef.current = [];
        }
      }
    };

    // Observer les changements de classes CSS
    const mutationObserver = new MutationObserver(mutations => {
      mutations.forEach(mutation => {
        if (
          mutation.type === 'attributes' &&
          (mutation.attributeName === 'class' ||
            mutation.attributeName === 'hidden')
        ) {
          checkSlideState();
        }
      });
    });

    // Observer les changements d'état des slides
    if (observerRef.current) {
      mutationObserver.observe(observerRef.current, {
        attributes: true,
        attributeFilter: ['class', 'hidden']
      });
    }

    // Vérifier l'état initial
    checkSlideState();

    return () => {
      // Nettoyer toutes les animations en cours
      timeoutsRef.current.forEach(timeout => clearTimeout(timeout));
      intervalsRef.current.forEach(interval => clearInterval(interval));
      mutationObserver.disconnect();
    };
  }, [startAnimations]);

  const styles = {
    heading: {
      color: 'white'
    },
    statCard: {
      backgroundColor: 'rgba(255, 255, 255, 0.1)',
      padding: '20px',
      borderRadius: '10px',
      margin: '15px',
      textAlign: 'center',
      border: '2px solid #4ecdc4',
      minWidth: '200px',
      transition: 'transform 0.3s ease, box-shadow 0.3s ease'
    },
    statNumber: {
      fontSize: '2.5em',
      fontWeight: 'bold',
      color: '#4ecdc4',
      margin: '10px 0',
      fontFamily: 'monospace'
    },
    statText: {
      fontSize: '1.1em',
      color: 'white'
    },
    statSource: {
      fontSize: '0.8em',
      color: '#4ecdc4',
      fontStyle: 'italic',
      textDecoration: 'underline',
      cursor: 'pointer'
    }
  };

  const formatNumber = (value, type) => {
    switch (type) {
      case 'attacks':
        return `${Math.round(value)}M`;
      case 'cost':
        return `$${value.toFixed(1)}T`;
      case 'jobs':
        return `${value.toFixed(1)}M`;
      case 'humanError':
        return `${Math.round(value)}%`;
      case 'detectionTime':
        return `${Math.round(value)} jours`;
      case 'breachCost':
        return `$${value.toFixed(2)}M`;
      default:
        return value.toString();
    }
  };

  const statistics = [
    {
      number: formatNumber(animatedValues.attacks, 'attacks'),
      text: CYBERSECURITY_STATS.dailyAttacks.text,
      source: CYBERSECURITY_STATS.dailyAttacks.source,
      url: CYBERSECURITY_STATS.dailyAttacks.url
    },
    {
      number: formatNumber(animatedValues.cost, 'cost'),
      text: CYBERSECURITY_STATS.globalCost.text,
      source: CYBERSECURITY_STATS.globalCost.source,
      url: CYBERSECURITY_STATS.globalCost.url
    },
    {
      number: formatNumber(animatedValues.jobs, 'jobs'),
      text: CYBERSECURITY_STATS.unfilledPositions.text,
      source: CYBERSECURITY_STATS.unfilledPositions.source,
      url: CYBERSECURITY_STATS.unfilledPositions.url
    },
    {
      number: formatNumber(animatedValues.humanError, 'humanError'),
      text: CYBERSECURITY_STATS.humanError.text,
      source: CYBERSECURITY_STATS.humanError.source,
      url: CYBERSECURITY_STATS.humanError.url
    },
    {
      number: formatNumber(animatedValues.detectionTime, 'detectionTime'),
      text: CYBERSECURITY_STATS.detectionTime.text,
      source: CYBERSECURITY_STATS.detectionTime.source,
      url: CYBERSECURITY_STATS.detectionTime.url
    },
    {
      number: formatNumber(animatedValues.breachCost, 'breachCost'),
      text: CYBERSECURITY_STATS.breachCost.text,
      source: CYBERSECURITY_STATS.breachCost.source,
      url: CYBERSECURITY_STATS.breachCost.url
    }
  ];

  return (
    <section ref={observerRef}>
      <section data-background-color="CadetBlue" data-statistics>
        <h1 style={styles.heading}>Statistiques cybersécurité 2024-2025</h1>
        <p style={{ color: 'white', fontSize: '1.2em' }}>
          Quelques chiffres qui illustrent l'ampleur du défi
        </p>
      </section>

      <section>
        <h2>Impact économique et humain</h2>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-around',
            flexWrap: 'wrap',
            alignItems: 'stretch'
          }}>
          {statistics.slice(0, 3).map((stat, idx) => (
            <div key={idx} style={styles.statCard}>
              <div style={styles.statNumber}>{stat.number}</div>
              <div style={styles.statText}>{stat.text}</div>
              <div
                style={styles.statSource}
                onClick={() => window.open(stat.url, '_blank')}
                title="Cliquer pour voir la source">
                {stat.source}
              </div>
            </div>
          ))}
        </div>
      </section>

      <section>
        <h2>Défis opérationnels</h2>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-around',
            flexWrap: 'wrap',
            alignItems: 'stretch'
          }}>
          {statistics.slice(3).map((stat, idx) => (
            <div key={idx} style={styles.statCard}>
              <div style={styles.statNumber}>{stat.number}</div>
              <div style={styles.statText}>{stat.text}</div>
              <div
                style={styles.statSource}
                onClick={() => window.open(stat.url, '_blank')}
                title="Cliquer pour voir la source">
                {stat.source}
              </div>
            </div>
          ))}
        </div>
      </section>

      <section>
        <h2>Tendances 2024-2025</h2>
        <div style={{ textAlign: 'left', maxWidth: '800px', margin: '0 auto' }}>
          <h4>
            <span role="img" aria-label="Rocket">
              🚀
            </span>{' '}
            Croissance exponentielle
          </h4>
          <ul>
            <li>
              <strong>+35%</strong> d'attaques par an
              <a
                href="https://www.accenture.com/us-en/insights/security/cybercrime-study"
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  color: '#4ecdc4',
                  textDecoration: 'underline',
                  marginLeft: '10px'
                }}>
                (Accenture)
              </a>
            </li>
            <li>
              <strong>+50%</strong> d'attaques IA générative
              <a
                href="https://www.darktrace.com/en/blog/ai-generated-phishing-attacks-increase-by-135-percent"
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  color: '#4ecdc4',
                  textDecoration: 'underline',
                  marginLeft: '10px'
                }}>
                (Darktrace)
              </a>
            </li>
            <li>
              <strong>+200%</strong> d'attaques sur l'IoT
              <a
                href="https://www.fortinet.com/blog/threat-research/iot-attacks-increase-41-percent"
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  color: '#4ecdc4',
                  textDecoration: 'underline',
                  marginLeft: '10px'
                }}>
                (Fortinet)
              </a>
            </li>
          </ul>

          <h4>
            <span role="img" aria-label="Target">
              🎯
            </span>{' '}
            Secteurs les plus ciblés
          </h4>
          <ul>
            <li>
              <strong>Santé :</strong> 25% des attaques (données sensibles)
              <a
                href="https://www.verizon.com/business/resources/reports/dbir/"
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  color: '#4ecdc4',
                  textDecoration: 'underline',
                  marginLeft: '10px'
                }}>
                (Verizon DBIR)
              </a>
            </li>
            <li>
              <strong>Finance :</strong> 20% des attaques (motivation
              financière)
              <a
                href="https://www.verizon.com/business/resources/reports/dbir/"
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  color: '#4ecdc4',
                  textDecoration: 'underline',
                  marginLeft: '10px'
                }}>
                (Verizon DBIR)
              </a>
            </li>
            <li>
              <strong>Éducation :</strong> 15% des attaques (faible sécurité)
              <a
                href="https://www.verizon.com/business/resources/reports/dbir/"
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  color: '#4ecdc4',
                  textDecoration: 'underline',
                  marginLeft: '10px'
                }}>
                (Verizon DBIR)
              </a>
            </li>
          </ul>
        </div>
      </section>
    </section>
  );
}
